/**
 * QC Grade Badge Component
 * Displays quality control grades with color-coded visual indicators
 *
 * @author SAUBHAGYA Development Team
 * @since Story 3-3
 *
 * @description
 * Visual badge component for displaying QC grades (A-D) with:
 * - Color-coded grades (Green/Blue/Yellow/Red)
 * - Multiple size variants (sm/md/lg)
 * - Optional full label display
 * - Full accessibility support (ARIA, dark mode)
 *
 * @example
 * ```tsx
 * // Basic usage (Grade letter only)
 * <QCGradeBadge grade="A" />
 *
 * // With full label
 * <QCGradeBadge grade="A" showLabel />
 *
 * // Custom size
 * <QCGradeBadge grade="B" size="lg" />
 * ```
 */

import { cn } from '@/lib/utils';

export type Grade = 'A' | 'B' | 'C' | 'D';
type Size = 'sm' | 'md' | 'lg';

export interface QCGradeBadgeProps {
    /**
     * Quality grade to display (A=Premium, B=Standard, C=Basic, D=Poor)
     */
    grade: Grade;

    /**
     * Visual size of the badge
     * @default 'md'
     */
    size?: Size;

    /**
     * Show full label text instead of just grade letter
     * @default false
     */
    showLabel?: boolean;

    /**
     * Additional CSS classes
     */
    className?: string;
}

/**
 * Grade configuration with colors and labels
 * AC-1: Grade Colors
 */
const gradeConfig: Record<Grade, {
    label: string;
    bgColor: string;
    textColor: string;
    darkBgColor: string;
    darkTextColor: string;
}> = {
    A: {
        label: 'Premium',
        bgColor: 'bg-green-100',
        textColor: 'text-green-800',
        darkBgColor: 'dark:bg-green-900',
        darkTextColor: 'dark:text-green-200'
    },
    B: {
        label: 'Standard',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-800',
        darkBgColor: 'dark:bg-blue-900',
        darkTextColor: 'dark:text-blue-200'
    },
    C: {
        label: 'Basic',
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800',
        darkBgColor: 'dark:bg-yellow-900',
        darkTextColor: 'dark:text-yellow-200'
    },
    D: {
        label: 'Poor',
        bgColor: 'bg-red-100',
        textColor: 'text-red-800',
        darkBgColor: 'dark:bg-red-900',
        darkTextColor: 'dark:text-red-200'
    }
};

/**
 * Size configuration for badge dimensions
 * AC-2: Size Variants
 */
const sizeConfig: Record<Size, {
    height: string;
    padding: string;
    fontSize: string;
    minWidth: string;
}> = {
    sm: {
        height: 'h-5',
        padding: 'px-1.5',
        fontSize: 'text-xs',
        minWidth: 'min-w-5'
    },
    md: {
        height: 'h-6',
        padding: 'px-2',
        fontSize: 'text-sm',
        minWidth: 'min-w-6'
    },
    lg: {
        height: 'h-8',
        padding: 'px-3',
        fontSize: 'text-base',
        minWidth: 'min-w-8'
    }
};

/**
 * QC Grade Badge Component
 *
 * @implements AC-1 Grade Colors (Green/Blue/Yellow/Red with dark mode)
 * @implements AC-2 Size Variants (sm/md/lg)
 * @implements AC-3 Label Display (grade letter or full label)
 * @implements AC-4 Accessibility (role, aria-label, dark mode)
 */
export function QCGradeBadge({
    grade,
    size = 'md',
    showLabel = false,
    className
}: QCGradeBadgeProps) {
    const gradeInfo = gradeConfig[grade];
    const sizeInfo = sizeConfig[size];

    // AC-3: Label Display
    const displayText = showLabel
        ? `Grade ${grade} - ${gradeInfo.label}`
        : grade;

    // AC-4: Accessibility
    const ariaLabel = `Quality grade ${grade}, ${gradeInfo.label}`;

    return (
        <span
            role="status"
            aria-label={ariaLabel}
            className={cn(
                // Base styles
                'inline-flex items-center justify-center rounded-full font-semibold',

                // AC-2: Size variant styles
                sizeInfo.height,
                sizeInfo.padding,
                sizeInfo.fontSize,

                // Only apply min-width when showing grade letter only
                showLabel ? '' : sizeInfo.minWidth,

                // AC-1: Grade colors (light mode)
                gradeInfo.bgColor,
                gradeInfo.textColor,

                // AC-4: Dark mode support
                gradeInfo.darkBgColor,
                gradeInfo.darkTextColor,

                // Custom classes
                className
            )}
        >
            {displayText}
        </span>
    );
}

export default QCGradeBadge;
